import { Clock, PackagePlus, ShoppingCart } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { useEffect, useRef, useState, type CSSProperties } from 'react'

import { EmptyState, PosButton } from '@/components/ui'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui'
import { CartPanel } from '@/components/pos/cart/CartPanel'
import { CartResizeHandle } from '@/components/pos/cart/CartResizeHandle'
import { CategoryChips } from '@/components/pos/catalog/CategoryChips'
import { ProductGrid } from '@/components/pos/catalog/ProductGrid'
import { ProductSearch } from '@/components/pos/catalog/ProductSearch'
import { CustomerDialog } from '@/components/pos/checkout/CustomerDialog'
import { DiscountDialog } from '@/components/pos/checkout/DiscountDialog'
import { PaymentDialog } from '@/components/pos/checkout/PaymentDialog'
import { ReceiptDialog } from '@/components/pos/checkout/ReceiptDialog'
import { NewProductDialog } from '@/components/pos/catalog/NewProductDialog'
import { HeldOrdersDialog } from '@/components/pos/orders/HeldOrdersDialog'
import { SyncSignInDialog } from '@/components/layout/SyncSignInDialog'
import { useCart } from '@/contexts/CartContext'
import { useHeldOrders } from '@/contexts/HeldOrdersContext'
import { useRegisterContext } from '@/contexts/RegisterContext'
import { useSession, useSyncState } from '@/contexts/SessionContext'
import { useAddToCart, useCategories, useProducts } from '@/hooks/features/catalog'
import { useDiscountCap } from '@/hooks/features/register'
import { useBarcodeScanner } from '@/hooks/ui/useBarcodeScanner'
import { useCompanionScans } from '@/services/companion/scans'
import { sellColumns, useCartWidth } from '@/hooks/ui/useCartWidth'
import { useDebounce } from '@/hooks/ui/useDebounce'
import { useHotkeys } from '@/hooks/ui/useHotkeys'
import { cn } from '@/lib/utils'
import { lineNet } from '@/services'
import { PosModel } from '@/types'
import { formatMoney } from '@/utils'

type Panel = 'customer' | 'discount' | 'lineDiscount' | 'pay' | 'held' | 'cart' | 'newProduct' | null

/**
 * The till. Catalog on the left (search, categories, grid), ticket on the right. Every dialog returns focus to the
 * search box so the next scan always lands. Below `lg` the ticket collapses into a bottom bar that opens it full screen.
 */
export const SellPage = () => {
  const register = useRegisterContext()
  const { stage, can } = useSession()
  const sync = useSyncState()
  // Sync waiting for a password is asked about between sales, when the receipt closes (§15.10 Q30).
  const [askSignIn, setAskSignIn] = useState(false)
  // On a real device a sale belongs to a shift; the demo register is always open.
  const shiftClosed = stage !== 'demo' && !register.opened_at
  const cart = useCart()
  const discountCap = useDiscountCap()
  const { held, hold } = useHeldOrders()
  const { add, addByCode } = useAddToCart()

  const searchRef = useRef<HTMLInputElement>(null)
  const gridRef = useRef<HTMLDivElement>(null)
  const cartRef = useRef<HTMLDivElement>(null)
  const cartWidth = useCartWidth()
  const [search, setSearch] = useState('')
  const [categoryId, setCategoryId] = useState<string>()
  const [panel, setPanel] = useState<Panel>(null)
  const [lineId, setLineId] = useState<string>()
  const [receipt, setReceipt] = useState<PosModel.Order | null>(null)

  // "Held carts" in the register's top bar opens the held list here.
  const location = useLocation()
  const navigate = useNavigate()
  useEffect(() => {
    if ((location.state as { held?: boolean } | null)?.held) {
      setPanel('held')
      navigate('.', { replace: true, state: null })
    }
  }, [location.state, navigate])

  const debounced = useDebounce(search)
  const { data: categories } = useCategories()
  const { data: products, isLoading } = useProducts({ search: debounced || undefined, category_id: categoryId })

  const close = () => {
    setPanel(null)
    requestAnimationFrame(() => searchRef.current?.focus())
  }
  const open = (p: Panel) => setPanel(p)
  const empty = cart.lines.length === 0

  const onHold = () => {
    if (hold()) toast.success('Order held', { description: 'Resume it from Held orders (F7).' })
  }
  const onClear = () => {
    if (!empty) {
      const snapshot = { lines: cart.lines, customer: cart.customer, discount: cart.discount }
      cart.clear()
      toast('Cart cleared', { action: { label: 'Undo', onClick: () => cart.load(snapshot) } })
    }
  }
  const onPay = () => !empty && open('pay')
  const nudgeActive = (delta: number) => {
    const line = cart.lines.find((l) => l.product._id === cart.activeId) ?? cart.lines.at(-1)
    if (line) cart.setQty(line.product._id, line.quantity + delta)
  }

  const dialogOpen = panel !== null && panel !== 'cart'
  useHotkeys(
    {
      search: () => searchRef.current?.focus(),
      customer: () => open('customer'),
      discount: () => !empty && open('discount'),
      hold: onHold,
      held: () => open('held'),
      pay: onPay,
      clear: onClear,
      qtyUp: () => nudgeActive(1),
      qtyDown: () => nudgeActive(-1),
    },
    !dialogOpen && !receipt && !shiftClosed
  )
  useBarcodeScanner((code) => addByCode(code), !dialogOpen && !receipt && !shiftClosed)
  useCompanionScans((code) => addByCode(code), !dialogOpen && !receipt && !shiftClosed)

  const onSubmitSearch = async (value: string) => {
    if (await addByCode(value, products?.items)) setSearch('')
  }
  // In the box while it is looked up, as the user sees it; cleared once it is in the cart.
  const onCameraScan = async (code: string) => {
    setSearch(code)
    if (await addByCode(code)) setSearch('')
  }

  const line = cart.lines.find((l) => l.product._id === lineId)
  const afterLineDiscounts = cart.lines.reduce((s, l) => s + lineNet(l), 0)

  const cartPanel = (
    <CartPanel
      onPickCustomer={() => open('customer')}
      onOrderDiscount={() => open('discount')}
      onLineDiscount={(id) => {
        setLineId(id)
        open('lineDiscount')
      }}
      onHold={onHold}
      onClear={onClear}
      onResume={() => open('held')}
      onPay={onPay}
      heldCount={held.length}
    />
  )

  // Nothing can be rung up before the drawer is opened; the cart stays as it is for when the shift opens.
  if (shiftClosed) {
    return (
      <div className="grid h-full place-items-center bg-subdued p-4">
        <EmptyState icon={Clock} title="No shift is open on this terminal">
          {can('pos:open_shift') ? (
            <PosButton size="touch" className="mt-2 min-w-48" onClick={() => navigate('/register/shift')}>
              Open shift
            </PosButton>
          ) : (
            'Ask a manager to open a shift before selling.'
          )}
        </EmptyState>
      </div>
    )
  }

  return (
    <div
      ref={gridRef}
      className={cn(
        'grid h-full grid-cols-[minmax(0,1fr)]',
        cartWidth.width ? 'lg:grid-cols-[var(--sell-cols)]' : 'lg:grid-cols-[minmax(0,16fr)_minmax(380px,9fr)]'
      )}
      style={cartWidth.width ? ({ '--sell-cols': sellColumns(cartWidth.width) } as CSSProperties) : undefined}
    >
      <section className="flex min-h-0 flex-col">
        <header className="space-y-3 border-b border-border px-4 pb-3 pt-3">
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <ProductSearch ref={searchRef} value={search} onChange={setSearch} onSubmit={onSubmitSearch} onScan={onCameraScan} />
            </div>
            <PosButton size="touch" className="h-12 rounded-sm" onClick={() => search.trim() && onSubmitSearch(search.trim())} disabled={!search.trim()}>
              Add
            </PosButton>
          </div>
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <CategoryChips categories={categories ?? []} value={categoryId} onChange={setCategoryId} />
            </div>
            {stage !== 'demo' && can('products:create') && can('product_variations:create') && (
              <PosButton variant="outline" className="mb-1 h-9 shrink-0 rounded-sm" onClick={() => open('newProduct')}>
                <PackagePlus /> New product
              </PosButton>
            )}
          </div>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto bg-subdued p-4">
          <ProductGrid products={products?.items} isLoading={isLoading} onAdd={(p) => add(p)} />
        </div>

        <div className="border-t border-border bg-card p-3 lg:hidden">
          <PosButton size="xl" className="w-full justify-between" onClick={() => open('cart')}>
            <span className="flex items-center gap-2">
              <ShoppingCart /> {cart.totals.itemCount} items
            </span>
            <span className="tabular-nums">{formatMoney(cart.totals.total)}</span>
          </PosButton>
        </div>
      </section>

      <div ref={cartRef} className="relative hidden min-h-0 border-l border-border lg:block">
        <CartResizeHandle container={gridRef} cart={cartRef} width={cartWidth.width} onChange={cartWidth.setWidth} onCommit={cartWidth.commit} />
        {cartPanel}
      </div>

      <Dialog open={panel === 'cart'} onOpenChange={(o) => !o && close()}>
        <DialogContent hideClose className="h-[90vh] max-h-[90vh] gap-0 overflow-hidden p-0 lg:hidden">
          <DialogTitle className="sr-only">Cart</DialogTitle>
          {cartPanel}
        </DialogContent>
      </Dialog>

      <CustomerDialog open={panel === 'customer'} onOpenChange={(o) => !o && close()} selected={cart.customer} onSelect={cart.setCustomer} />
      <DiscountDialog
        open={panel === 'discount'}
        onOpenChange={(o) => !o && close()}
        title="Order discount"
        base={afterLineDiscounts}
        current={cart.discount}
        onApply={cart.setDiscount}
        cap={discountCap}
      />
      <DiscountDialog
        open={panel === 'lineDiscount' && !!line}
        onOpenChange={(o) => !o && close()}
        title={line ? `Discount · ${line.product.name}` : 'Item discount'}
        base={line ? line.product.price * line.quantity : 0}
        current={line?.discount}
        onApply={(d) => lineId && cart.setLineDiscount(lineId, d)}
        cap={discountCap}
      />
      <PaymentDialog open={panel === 'pay'} onOpenChange={(o) => !o && close()} onComplete={setReceipt} />
      <HeldOrdersDialog open={panel === 'held'} onOpenChange={(o) => !o && close()} />
      <NewProductDialog
        open={panel === 'newProduct'}
        onOpenChange={(o) => !o && close()}
        categories={categories ?? []}
        barcode={/^\d{8,14}$/.test(search.trim()) ? search.trim() : undefined}
        onCreated={(p) => {
          setSearch('')
          add(p)
        }}
      />
      <ReceiptDialog
        order={receipt}
        onClose={() => {
          setReceipt(null)
          if (stage !== 'demo' && sync.status === 'auth') setAskSignIn(true)
          else close()
        }}
      />
      <SyncSignInDialog
        open={askSignIn}
        onOpenChange={(o) => {
          setAskSignIn(o)
          if (!o) close()
        }}
      />
    </div>
  )
}
